import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowRight, RefreshCw, MessageCircle, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import { StewAvatar } from "@/components/stew/StewAvatar";

type RecordType = "resolution" | "journal" | "minutes";

interface Classification {
  record_type: RecordType;
  suggested_title: string;
  rationale: string;
  prefill: Record<string, any>;
}

const TYPE_LABEL: Record<RecordType, string> = {
  resolution: "Resolution",
  journal: "Journal Entry",
  minutes: "Meeting Minutes",
};

const TYPE_HINT: Record<RecordType, string> = {
  resolution: "Formal, third-party facing record",
  journal: "Routine record with source and reason",
  minutes: "Something that belongs in a meeting",
};

const PLACEHOLDERS = [
  "We decided to open a bank account for the LLC",
  "Can the Association pay for my phone?",
  "We met Tuesday and approved the budget",
];

const QUESTION_STARTERS = /^(what|how|why|when|where|who|can|could|should|would|do|does|did|is|are|am|may|might|will)\b/i;

function looksLikeQuestion(text: string): boolean {
  const t = text.trim();
  if (!t) return false;
  if (t.endsWith("?")) return true;
  return QUESTION_STARTERS.test(t);
}

interface Props {
  unaId: string;
  compact?: boolean;
  tone?: "calm" | "tool";
}

export const GovernanceIntentIntake = ({ unaId, compact = false, tone = "tool" }: Props) => {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Classification | null>(null);
  const [overrideType, setOverrideType] = useState<RecordType | null>(null);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Rotate the placeholder every 4s while empty and unfocused-friendly.
  useEffect(() => {
    if (text || result) return;
    const t = setInterval(() => {
      setPlaceholderIndex((i) => (i + 1) % PLACEHOLDERS.length);
    }, 4000);
    return () => clearInterval(t);
  }, [text, result]);

  const routeToStew = (question: string) => {
    const q = question.trim();
    if (!q) return;
    navigate(`/support/stew?ask=${encodeURIComponent(q)}`);
  };

  const submit = async () => {
    const q = text.trim();
    if (!q || !unaId || loading) return;

    // Fast path: if it clearly reads as a question, hand off to Stew.
    if (looksLikeQuestion(q)) {
      routeToStew(q);
      return;
    }

    setLoading(true);
    setResult(null);
    setOverrideType(null);
    try {
      const { data, error } = await supabase.functions.invoke("classify-governance-intent", {
        body: { unaId, text: q },
      });
      if (error) {
        const msg = (error as any)?.context
          ? await (error as any).context.json().then((j: any) => j?.error).catch(() => null)
          : null;
        throw new Error(msg || error.message || "Could not classify");
      }
      const classification = data?.classification as Classification | undefined;
      if (!classification?.record_type) {
        // Unclassifiable — hand off to Stew rather than failing.
        routeToStew(q);
        return;
      }
      setResult(classification);
    } catch (e: any) {
      // Silent handoff to Stew on classifier failure.
      routeToStew(q);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setOverrideType(null);
    setText("");
    setTimeout(() => textareaRef.current?.focus(), 30);
  };

  const handleCreate = () => {
    if (!result) return;
    const chosenType = overrideType || result.record_type;
    const prefillId = crypto.randomUUID();
    const seed = {
      __source: "governance_intake",
      __intent_text: text.trim(),
      __suggested_title: result.suggested_title,
      ...(result.prefill || {}),
    };

    try {
      sessionStorage.setItem(`gh_intent_prefill_${prefillId}`, JSON.stringify(seed));
    } catch {
      // ignore quota
    }

    const workflowKey =
      chosenType === "resolution"
        ? "writing_resolution"
        : chosenType === "journal"
          ? "record_journal_transaction"
          : "recording_minutes";

    navigate(`/workflows?workflow=${workflowKey}&gh_prefill=${prefillId}`);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const cardClass = tone === "calm" ? "border-border/60" : "border-gold/40";

  return (
    <Card className={cardClass}>
      <CardHeader className={compact ? "pb-3" : undefined}>
        <div className="flex items-center gap-3">
          <StewAvatar size={compact ? 28 : 32} />
          <div className="min-w-0">
            <CardTitle className={compact ? "text-base" : "text-lg"}>
              What do you need?
            </CardTitle>
            <CardDescription className="mt-0.5">
              Say it in plain words. I can answer a question, or start the right record for you.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!result && (
          <div className="relative">
            <Textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={PLACEHOLDERS[placeholderIndex]}
              rows={compact ? 2 : 3}
              disabled={loading}
              className="resize-none pr-12"
              aria-label="Ask a question or describe what happened"
            />
            <Button
              type="button"
              size="icon"
              onClick={submit}
              disabled={loading || !text.trim()}
              className="absolute bottom-2 right-2 h-8 w-8"
              aria-label="Send"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowUp className="w-4 h-4" />
              )}
            </Button>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="bg-gold/10 border-gold/40">
                  {TYPE_LABEL[overrideType || result.record_type]}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {TYPE_HINT[overrideType || result.record_type]}
                </span>
              </div>
              <div className="text-sm font-medium">{result.suggested_title}</div>
              <p className="text-xs text-muted-foreground italic">{result.rationale}</p>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-1 text-xs text-muted-foreground flex-wrap">
                <span>Change type:</span>
                {(["resolution", "journal", "minutes"] as RecordType[]).map((t) => {
                  const active = (overrideType || result.record_type) === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setOverrideType(t)}
                      className={`px-2 py-0.5 rounded text-xs transition-colors ${
                        active
                          ? "bg-foreground text-background"
                          : "hover:bg-muted"
                      }`}
                    >
                      {TYPE_LABEL[t]}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="ghost" onClick={reset}>
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Start over
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => routeToStew(text)}
                >
                  <MessageCircle className="w-3 h-3 mr-1" />
                  Ask Stew instead
                </Button>
                <Button size="sm" onClick={handleCreate}>
                  Start this record
                  <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GovernanceIntentIntake;
